import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IVideoClip, IUserModel, ModelType, ResourceType, ResourceStatus, VideoClipStatus, IVideoClipResource } from '@packages/global';
import { videoClipResourceApi } from '../../../services/user/videoClipResource.api';
import { videoClipApi } from '../../../services/user/videoclip.api';
import { useDialog } from '../../../hooks/useDialog';
import { getAudioDuration } from '../../../utils/audioUtils';
import { useToast } from '../../../stores/toastStore';
import { XMarkIcon, SpeakerWaveIcon, ArrowUpIcon, CheckIcon, MicrophoneIcon, PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import AudioUpload from '../../common/AudioUpload';
import AudioPlayer from '../../common/AudioPlayer';
import { EmotionSelector, AlphaSlider, SpeedSlider, CoinPriceDisplay } from '../../common/form';
import AudioEditForm from './AudioEditForm';

type AudioTab = 'tts' | 'audioEdit';

interface AudioGenerationFormProps {
  videoClip: IVideoClip;
  models: IUserModel[];
  modelsLoading: boolean;
  refreshVideoClip?: (clipId: string) => Promise<void>;
  resources?: IVideoClipResource[];
  isPolling?: boolean;
  onTriggerPoll?: () => Promise<void>;
  canvasMode?: boolean;
  canvasActiveTab?: AudioTab;
  externalAddAudio?: { url: string; nonce: number };
  onReferenceAudioCleared?: (url: string) => void;
  onCanvasSubmitSuccess?: () => void;
  initialParams?: Record<string, unknown>;
}

const statusColorConfig: Record<string, string> = {
  [ResourceStatus.PENDING]: 'text-gray-400',
  [ResourceStatus.GENERATING]: 'text-blue-400',
  [ResourceStatus.COMPLETED]: 'text-green-400',
  [ResourceStatus.FAILED]: 'text-red-400'
};

export default function AudioGenerationForm({
  videoClip,
  models,
  modelsLoading,
  refreshVideoClip,
  resources: externalResources = [],
  isPolling = false,
  onTriggerPoll,
  canvasMode = false,
  canvasActiveTab,
  externalAddAudio,
  onReferenceAudioCleared,
  onCanvasSubmitSuccess,
  initialParams
}: AudioGenerationFormProps) {
  const [activeTab, setActiveTab] = useState<AudioTab>(canvasActiveTab || 'tts');

  const [formData, setFormData] = useState({
    prompt: '',
    spkAudioUrl: '',
    emoAudioUrl: '',
    emoAlpha: 0.5,
    emoText: '',
    matchReferenceDuration: false,
    modelId: '',
    speedFactor: 1.72
  });
  const [showSpkAudioUpload, setShowSpkAudioUpload] = useState(false);
  const audioUploadRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { alert, confirm } = useDialog();
  const { success, error } = useToast();
  const { t } = useTranslation(['editor', 'common']);

  const ttsModels = models.filter(m => m.modelType === ModelType.TEXT_TO_SPEECH);

  const ttsResources = externalResources
    .filter(r => r.resourceType === ResourceType.SCRIPT_AUDIO)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ttsResources.length]);

  useEffect(() => {
    setFormData({
      prompt: videoClip.script || '',
      spkAudioUrl: '',
      emoAudioUrl: '',
      emoAlpha: 0.5,
      emoText: '',
      matchReferenceDuration: false,
      modelId: ttsModels.length > 0 ? ttsModels[0].id : '',
      speedFactor: 1.72
    });
  }, [videoClip.id, videoClip.script]);

  useEffect(() => {
    if (videoClip.lastAudioParams) {
      try {
        const params = JSON.parse(videoClip.lastAudioParams);
        handleReuseParams(params);
      } catch { /* ignore */ }
    }
  }, [videoClip.id]);

  const lastExternalAudioNonceRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!externalAddAudio) return;
    if (lastExternalAudioNonceRef.current === externalAddAudio.nonce) return;
    lastExternalAudioNonceRef.current = externalAddAudio.nonce;
    if (!externalAddAudio.url) return;

    setFormData(prev => ({
      ...prev,
      spkAudioUrl: externalAddAudio.url,
      matchReferenceDuration: false,
    }));
  }, [externalAddAudio]);

  const clearReferenceAudio = () => {
    const removedUrl = formData.spkAudioUrl;
    if (removedUrl) {
      onReferenceAudioCleared?.(removedUrl);
    }
    setFormData(prev => ({ ...prev, spkAudioUrl: '', matchReferenceDuration: false }));
  };

  const handleReuseParams = (params: any) => {
    setFormData(prev => ({
      ...prev,
      prompt: params.text || params.prompt || prev.prompt,
      spkAudioUrl: params.spkAudioUrl || prev.spkAudioUrl,
      emoAudioUrl: params.emoAudioUrl || prev.emoAudioUrl,
      emoAlpha: params.emoAlpha || prev.emoAlpha,
      emoText: params.emoText || prev.emoText,
      matchReferenceDuration: params.matchReferenceDuration || prev.matchReferenceDuration,
      modelId: params.modelId || prev.modelId
    }));
  };

  const initialParamsAppliedRef = useRef(false);
  useEffect(() => {
    if (!initialParams || initialParamsAppliedRef.current) return;
    initialParamsAppliedRef.current = true;
    handleReuseParams(initialParams);
  }, [initialParams]);

  const handleApplyAudio = async (audioUrl: string) => {
    try {
      let audioDuration: number | undefined;
      try { audioDuration = await getAudioDuration(audioUrl); } catch { /* ignore */ }

      const updateData: any = { scriptAudioUrl: audioUrl, status: VideoClipStatus.COMPLETED };
      if (audioDuration && audioDuration > 0 && !videoClip.videoUrl) {
        if (audioDuration > (videoClip.duration || 0)) updateData.duration = audioDuration;
      }

      await videoClipApi.updateVideoClip(videoClip.id, updateData);
      success(t('common:audioApplied'));
      if (refreshVideoClip) await refreshVideoClip(videoClip.id);
      if (onTriggerPoll) await onTriggerPoll();
    } catch {
      alert(t('common:applyFailed'));
    }
  };

  const handleDeleteResource = async (resourceId: string) => {
    const confirmed = await confirm(t('common:deleteResourceConfirm'), t('common:deleteResource'), 'danger');
    if (!confirmed) return;
    try {
      await videoClipResourceApi.deleteResource(resourceId);
      success(t('common:resourceDeleted'));
      if (onTriggerPoll) await onTriggerPoll();
    } catch {
      error(t('common:deleteFailed'));
    }
  };

  const handleTtsGenerate = async () => {
    if (!formData.modelId) { alert(t('audioGenForm.selectModel')); return; }
    if (!formData.prompt.trim()) { alert(t('audioGenForm.enterText')); return; }
    if (!formData.spkAudioUrl) { alert(t('audioGenForm.uploadRefAudioRequired')); return; }
    try {
      await videoClipResourceApi.generateScriptAudio(videoClip.id, { ...formData });
      success(t('audioGenForm.taskSubmitted'));
      setFormData(prev => ({ ...prev, prompt: '' }));
      onCanvasSubmitSuccess?.();
      if (onTriggerPoll) setTimeout(() => onTriggerPoll!(), 1000);
    } catch {
      error(t('common:submitFailed'));
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Tab 栏 */}
      {!canvasMode && (
        <div className="flex-shrink-0 px-4 py-2 border-b border-gray-800 bg-gray-800/30">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('tts')}
              className={`px-4 py-2 text-sm rounded-lg transition-colors cursor-pointer ${activeTab === 'tts' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'}`}
            >
              {t('audioGenForm.ttsGeneration')}
            </button>
            <button
              onClick={() => setActiveTab('audioEdit')}
              className={`px-4 py-2 text-sm rounded-lg transition-colors cursor-pointer ${activeTab === 'audioEdit' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'}`}
            >
              {t('audioGenForm.audioEdit')}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'tts' ? (
        <>
          {/* 资源列表 */}
          {!canvasMode && (
            <div className="flex-1 overflow-y-auto px-4 py-3">
              <div className="space-y-6">
                {ttsResources.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <SpeakerWaveIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('audioGenForm.noRecords')}</p>
                    <p className="text-xs mt-1">{t('audioGenForm.startBelow')}</p>
                  </div>
                ) : (
                  ttsResources.map((resource) => {
                  let params: any = null;
                  try {
                    params = typeof resource.generationParams === 'string'
                      ? JSON.parse(resource.generationParams)
                      : resource.generationParams;
                  } catch { /* ignore */ }
                  const statusInfo = statusColorConfig[resource.status];
                  const isCompleted = resource.status === ResourceStatus.COMPLETED && resource.resultUrl;

                  return (
                    <div key={resource.id} className="relative space-y-3 border-2 border-dashed border-gray-800 rounded-lg p-2 group">
                      {params && (
                        <button
                          onClick={() => handleReuseParams(params)}
                          className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-white bg-gray-800/80 hover:bg-gray-700 rounded-lg backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100 z-10 cursor-pointer"
                          title={t('common:useParams')}
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      )}
                      {(params?.text || params?.prompt) && (
                        <div className="bg-gray-800 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                            <SpeakerWaveIcon className="h-4 w-4" />
                            <span>{t('audioGenForm.speechText')}</span>
                            {params.emoText && <span>（{params.emoText}）</span>}
                          </div>
                          <p className="text-gray-200 text-sm leading-relaxed">{params.text || params.prompt}</p>
                        </div>
                      )}
                      {isCompleted && (
                        <AudioPlayer src={resource.resultUrl!} />
                      )}
                      {(resource.status === ResourceStatus.GENERATING || resource.status === ResourceStatus.PENDING) && (
                        <div className="py-8 flex flex-col items-center gap-3 text-gray-400">
                          <div className="h-8 w-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                          <span className="text-sm">{t(`generationProgress.${resource.status === ResourceStatus.PENDING ? 'pending' : 'generating'}`)}...</span>
                        </div>
                      )}
                      {resource.status === ResourceStatus.FAILED && (
                        <div className="p-3 bg-red-900/30 rounded-lg text-red-400 text-sm">
                          {resource.errorMessage || t('common:generationFailed')}
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        {isCompleted && (
                          <button
                            onClick={() => handleApplyAudio(resource.resultUrl!)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded-lg transition-colors cursor-pointer"
                          >
                            <CheckIcon className="h-3 w-3" />
                            <span className="text-xs">{t('audioGenForm.applyAudio')}</span>
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteResource(resource.id)}
                          className="p-1.5 text-gray-400 hover:text-red-400 rounded-lg transition-colors opacity-0 group-hover:opacity-100 cursor-pointer ml-auto"
                          title={t('common:deleteResource')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
          )}

          {/* 底部固定输入区 */}
          <div className="flex-shrink-0 border-t border-gray-800 bg-gray-800/30">
            <div className="px-4 pt-3">
              <div className="flex items-center gap-3">
                <div className="relative" ref={audioUploadRef}>
                  {formData.spkAudioUrl ? (
                    <div className="relative w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center group">
                      <audio ref={(a) => { if (a) a.src = formData.spkAudioUrl; }} className="hidden" />
                      <button
                        onClick={(e) => {
                          const audio = e.currentTarget.parentElement?.querySelector('audio') as HTMLAudioElement;
                          if (audio) audio.paused ? audio.play() : audio.pause();
                        }}
                        className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                      >
                        <MicrophoneIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={clearReferenceAudio}
                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer"
                      >
                        <XMarkIcon className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowSpkAudioUpload(true)}
                      className="w-12 h-12 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                    >
                      <PlusIcon className="h-5 w-5 text-gray-400" />
                    </button>
                  )}
                  {showSpkAudioUpload && (
                    <div className="absolute bottom-full left-0 mb-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-20 p-3 min-w-[280px]">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-gray-400">{t('audioGenForm.uploadRefAudio')}</span>
                        <button onClick={() => setShowSpkAudioUpload(false)} className="text-gray-500 hover:text-gray-300 cursor-pointer">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <AudioUpload
                        projectId={videoClip.projectId}
                        episodeId={videoClip.episodeId}
                        showAudioSelector={false}
                        showToneSelector={true}
                        value=""
                        onUploadSuccess={(url) => {
                          if (!url && formData.spkAudioUrl) {
                            onReferenceAudioCleared?.(formData.spkAudioUrl);
                          }
                          setFormData(prev => ({ ...prev, spkAudioUrl: url }));
                          setShowSpkAudioUpload(false);
                        }}
                        placeholder={t('audioGenForm.uploadRefAudioPlaceholder')}
                      />
                    </div>
                  )}
                </div>
                {formData.emoAudioUrl && (
                  <div className="relative w-12 h-12 bg-purple-800/30 rounded-lg flex items-center justify-center">
                    <audio ref={(a) => { if (a) a.src = formData.emoAudioUrl; }} className="hidden" />
                    <button
                      onClick={(e) => {
                        const audio = e.currentTarget.parentElement?.querySelector('audio') as HTMLAudioElement;
                        if (audio) audio.paused ? audio.play() : audio.pause();
                      }}
                      className="text-purple-400 hover:text-purple-300 transition-colors cursor-pointer"
                    >
                      <MicrophoneIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setFormData(prev => ({ ...prev, emoAudioUrl: '' }))}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer"
                    >
                      <XMarkIcon className="h-2.5 w-2.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="px-4 pt-3">
              <textarea
                value={formData.prompt}
                onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
                className="w-full h-20 bg-gray-800 text-gray-200 text-sm resize-none outline-none placeholder-gray-500 rounded-lg p-3"
                placeholder={t('audioGenForm.textPlaceholder')}
              />
            </div>

            <div className="px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {!formData.matchReferenceDuration && (
                    <EmotionSelector
                      emoText={formData.emoText}
                      emoAudioUrl={formData.emoAudioUrl}
                      onEmoTextChange={(text) => setFormData(prev => ({ ...prev, emoText: text }))}
                      onEmoAudioChange={(url) => setFormData(prev => ({ ...prev, emoAudioUrl: url }))}
                      episodeId={videoClip.episodeId}
                    />
                  )}
                  {(formData.emoAudioUrl || formData.emoText) && !formData.matchReferenceDuration && (
                    <AlphaSlider
                      value={formData.emoAlpha}
                      onChange={(value) => setFormData(prev => ({ ...prev, emoAlpha: value }))}
                    />
                  )}
                  {!formData.matchReferenceDuration && (
                    <SpeedSlider
                      value={formData.speedFactor}
                      onChange={(value) => setFormData(prev => ({ ...prev, speedFactor: value }))}
                    />
                  )}
                  <button
                    onClick={() => formData.spkAudioUrl && setFormData(prev => ({ ...prev, matchReferenceDuration: !prev.matchReferenceDuration }))}
                    disabled={!formData.spkAudioUrl}
                    className={`p-1.5 rounded-lg transition-colors ${!formData.spkAudioUrl ? 'bg-gray-700 text-gray-600 cursor-not-allowed' : formData.matchReferenceDuration ? 'bg-blue-600 text-white cursor-pointer' : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300 cursor-pointer'}`}
                    title={!formData.spkAudioUrl ? t('audioGenForm.needRefAudioFirst') : t('audioGenForm.matchRefDuration')}
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 8v4l3 3" strokeLinecap="round" strokeLinejoin="round" />
                      <circle cx="12" cy="12" r="9" />
                    </svg>
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <CoinPriceDisplay modelId={formData.modelId} />
                  <button
                    onClick={handleTtsGenerate}
                    disabled={!formData.modelId || !formData.prompt || !formData.spkAudioUrl}
                    className={`p-2 rounded-full transition-colors ${!formData.modelId || !formData.prompt || !formData.spkAudioUrl ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-600 hover:bg-gray-500 text-white cursor-pointer'}`}
                  >
                    <ArrowUpIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <AudioEditForm
          videoClip={videoClip}
          models={models}
          modelsLoading={modelsLoading}
          refreshVideoClip={refreshVideoClip}
          resources={externalResources}
          onTriggerPoll={onTriggerPoll}
          canvasMode={canvasMode}
          externalAddAudio={externalAddAudio}
          onReferenceAudioCleared={onReferenceAudioCleared}
          onCanvasSubmitSuccess={onCanvasSubmitSuccess}
          initialParams={initialParams}
        />
      )}
    </div>
  );
}
